import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import api, { Organisation, CreateCampaignRequest, TargetDto } from '../services/api'
import LoadingSpinner from './common/LoadingSpinner'
import CampaignTargetSelector from './CampaignTargetSelector'
import { createLogger } from '../utils/createLogger'

const log = createLogger('NewCampaignModal')

interface NewCampaignModalProps {
  organisation: Organisation
  isTrialMode: boolean
  onClose: () => void
  onSuccess: () => void
}

type SelectionMethod = 'all' | 'random' | 'department' | 'manual'

const NewCampaignModal: React.FC<NewCampaignModalProps> = ({
  organisation,
  isTrialMode,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Target pool data
  const [availableTargets, setAvailableTargets] = useState<TargetDto[]>([])
  const [departments, setDepartments] = useState<string[]>([])
  const [loadingTargets, setLoadingTargets] = useState(false)
  
  // Form state
  const [formData, setFormData] = useState({
    campaign_display_name: '',
    campaign_difficulty: 'easy' as 'easy' | 'moderate' | 'hard',
    schedule_date: '',
    schedule_time: '',
    target_method: 'all' as SelectionMethod,
    target_sample_size: 0,
    selected_emails: [] as string[],
    selected_departments: [] as string[]
  })

  useEffect(() => {
    loadTargetPool()
  }, [])

  const loadTargetPool = async () => {
    const orgId = organisation.organisation_id || organisation.id
    if (!orgId) {
      log.warn('No organisation ID found', { organisation })
      return
    }
    
    log.info('Loading target pool', { orgId })
    
    try {
      setLoadingTargets(true)
      const response = await api.targetPool.list(orgId)
      
      log.info('Loaded target pool', { 
        targetCount: response.targets.length,
        departments: response.departments 
      })
      
      setAvailableTargets(response.targets)
      setDepartments(response.departments)
      setFormData(prev => ({ 
        ...prev, 
        target_sample_size: response.targets.length 
      }))
    } catch (error) {
      log.error('Failed to load target pool', error instanceof Error ? error : undefined, { orgId })
      setAvailableTargets([])
      setDepartments([])
    } finally {
      setLoadingTargets(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    try {
      setLoading(true)
      
      const orgId = organisation.organisation_id || organisation.id || ''
      
      // Build request data
      const requestData: CreateCampaignRequest = {
        campaign_display_name: formData.campaign_display_name,
        organisation_id: orgId,
        campaign_difficulty: formData.campaign_difficulty
      }
      
      // Handle schedule
      if (formData.schedule_date) {
        const scheduledDateTime = formData.schedule_time 
          ? `${formData.schedule_date}T${formData.schedule_time}:00Z`
          : `${formData.schedule_date}T09:00:00Z`
        
        requestData.campaign_start = scheduledDateTime
        requestData.sending_window_hours = 4
        requestData.tracking_period_days = 7
      }
      
      // Handle target selection
      if (formData.target_method === 'all') {
        requestData.target_selection_method = 'all'
        requestData.target_sample_size = availableTargets.length
        
      } else if (formData.target_method === 'random') {
        requestData.target_selection_method = 'random'
        requestData.target_sample_size = formData.target_sample_size
        
      } else if (formData.target_method === 'department') {
        // Convert departments to email list for manual selection
        const departmentEmails = availableTargets
          .filter(t => formData.selected_departments.includes(t.department || ''))
          .map(t => t.target_email)
        
        requestData.target_selection_method = 'manual'
        requestData.campaign_targets = departmentEmails
        
      } else if (formData.target_method === 'manual') {
        requestData.target_selection_method = 'manual'
        requestData.campaign_targets = formData.selected_emails
      }
      
      log.info('Creating campaign', { requestData })
      
      const response = await api.campaigns.create(requestData)
      
      log.info('Campaign created successfully', { response })
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Campaign creation failed:', error)
      
      let displayError = 'Failed to create campaign'
      
      if (error?.response?.status === 400 && error?.response?.data?.errors) {
        const errors = error.response.data.errors
        const errorMessages = Object.keys(errors)
          .filter(key => key !== '$')
          .map(key => errors[key])
          .flat()
          .join(', ')
        displayError = errorMessages || error?.response?.data?.title || displayError
      } else if (error?.response?.data?.error) {
        displayError = error.response.data.error
      } else if (error?.response?.data?.message) {
        displayError = error.response.data.message
      }
      
      log.error('Failed to create campaign', error instanceof Error ? error : undefined, { 
        error: displayError
      })
      
      setError(displayError)
    } finally {
      setLoading(false)
    }
  }

  const getTargetCount = () => {
    if (formData.target_method === 'all') {
      return availableTargets.length
    } else if (formData.target_method === 'random') {
      return formData.target_sample_size
    } else if (formData.target_method === 'department') {
      return availableTargets.filter(t => 
        formData.selected_departments.includes(t.department || '')
      ).length
    } else if (formData.target_method === 'manual') {
      return formData.selected_emails.length
    }
    return 0
  }

  const canSave = () => {
    if (!formData.campaign_display_name.trim()) return false
    
    const targetCount = getTargetCount()
    if (targetCount === 0) return false
    
    return true
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">New Campaign</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {isTrialMode && (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
              <strong className="text-yellow-900 dark:text-yellow-200">Trial Mode:</strong>
              <span className="text-yellow-800 dark:text-yellow-300 ml-1">
                This campaign will only send to your admin email.
              </span>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              <strong className="text-red-900 dark:text-red-200">Error:</strong>
              <span className="text-red-800 dark:text-red-300 ml-1">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Campaign Name
              </label>
              <input
                type="text"
                value={formData.campaign_display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, campaign_display_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Q1 Security Test"
                required
                autoFocus
              />
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Difficulty Level
              </label>
              <select
                value={formData.campaign_difficulty}
                onChange={(e) => setFormData(prev => ({ ...prev, campaign_difficulty: e.target.value as 'easy' | 'moderate' | 'hard' }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="easy">Easy</option>
                <option value="moderate">Moderate</option>
                <option value="hard">Hard</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Determines which phishing template will be selected
              </p>
            </div>

            {/* Target Selection */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Target Selection</h3>
              {loadingTargets ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <>
                  <CampaignTargetSelector
                    organisationId={organisation.organisation_id || organisation.id || ''}
                    availableTargets={availableTargets}
                    departments={departments}
                    loading={loadingTargets}
                    selectionMethod={formData.target_method}
                    selectedEmails={formData.selected_emails}
                    selectedDepartments={formData.selected_departments}
                    sampleSize={formData.target_sample_size}
                    onSelectionMethodChange={(method) => setFormData(prev => ({ ...prev, target_method: method }))}
                    onSelectedEmailsChange={(emails) => setFormData(prev => ({ ...prev, selected_emails: emails }))}
                    onSelectedDepartmentsChange={(depts) => setFormData(prev => ({ ...prev, selected_departments: depts }))}
                    onSampleSizeChange={(size) => setFormData(prev => ({ ...prev, target_sample_size: size }))}
                  />
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{getTargetCount()}</span> recipient{getTargetCount() !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Schedule (Optional) */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Schedule (Optional)
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Leave empty to save as draft. You can schedule it later.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.schedule_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, schedule_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Time</label>
                  <input
                    type="time"
                    value={formData.schedule_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, schedule_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {formData.schedule_date && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Emails will be sent randomly over a 4-hour window. Tracking continues for 7 days.
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !canSave()}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating...' : formData.schedule_date ? 'Create & Schedule' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NewCampaignModal
